import { z } from "zod"

/* The categorisation surface (PRD: engagement-analysis-layer, phase 4).
   These are the single definition of the request and response shapes: the API
   routes in apps/api/src/routes/categories import them directly, and the
   api-client parses responses through them, so the two can never disagree. */

/** Mirrors the categories.kind check constraint in packages/db. */
export const ApiCategoryKindSchema = z.enum([
  "income",
  "expense",
  "transfer",
  "other",
])

export type ApiCategoryKind = z.infer<typeof ApiCategoryKindSchema>

export const categoryNameMaxLength = 120
export const categoryRuleKeywordMaxLength = 80
export const categoryRuleKeywordsMax = 100
export const categoryRulePriorityMax = 1000
/** Generous for a hand-kept chart: hundreds of rules, never a data dump. */
export const categoriesCsvMaxLength = 200_000

/** Trimmed, non-empty keywords; matching is case-insensitive substring. */
const KeywordListSchema = z
  .array(z.string().trim().min(1).max(categoryRuleKeywordMaxLength))
  .max(categoryRuleKeywordsMax)

export const ApiCategoryRuleSchema = z.object({
  id: z.string().uuid(),
  categoryId: z.string().uuid(),
  /** Null applies the rule in every workspace; a value limits it to that
   * workspace (one client). */
  workspaceId: z.string().uuid().nullable(),
  includeKeywords: z.array(z.string()),
  excludeKeywords: z.array(z.string()),
  /** Lower number wins first. */
  priority: z.number().int(),
  isSeeded: z.boolean(),
  /**
   * Where the rule came from, so the editor can group by provenance.
   * `accepted` was written by the Review screen when someone accepted a
   * vendor; nobody should need to open this editor to get one.
   */
  origin: z.enum(["seeded", "manual", "accepted"]),
  archivedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
})

export type ApiCategoryRule = z.infer<typeof ApiCategoryRuleSchema>

/** One category with its rules embedded: the editor screen's whole row. */
export const ApiCategorySchema = z.object({
  id: z.string().uuid(),
  /** Null means organisation default, visible to every workspace. */
  workspaceId: z.string().uuid().nullable(),
  name: z.string(),
  kind: ApiCategoryKindSchema,
  /** Two levels maximum; a category with a parent cannot be a parent. */
  parentId: z.string().uuid().nullable(),
  isSeeded: z.boolean(),
  archivedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  rules: z.array(ApiCategoryRuleSchema),
})

export type ApiCategory = z.infer<typeof ApiCategorySchema>

/** Archived categories are included; the client decides whether to show them. */
export const ApiCategoryListResponseSchema = z.object({
  categories: z.array(ApiCategorySchema),
})

export type ApiCategoryListResponse = z.infer<
  typeof ApiCategoryListResponseSchema
>

export const ApiCreateCategoryBodySchema = z.object({
  name: z.string().trim().min(1).max(categoryNameMaxLength),
  kind: ApiCategoryKindSchema,
  parentId: z.string().uuid().optional(),
  /** Omitted means organisation default, visible to every workspace. */
  workspaceId: z.string().uuid().optional(),
})

export type ApiCreateCategoryBody = z.infer<typeof ApiCreateCategoryBodySchema>

export const ApiUpdateCategoryBodySchema = z.object({
  name: z.string().trim().min(1).max(categoryNameMaxLength).optional(),
  kind: ApiCategoryKindSchema.optional(),
  /** Null detaches the category from its parent. */
  parentId: z.string().uuid().nullable().optional(),
  /** True archives the category, false restores it. */
  archived: z.boolean().optional(),
})

export type ApiUpdateCategoryBody = z.infer<typeof ApiUpdateCategoryBodySchema>

export const ApiDeleteCategoryResponseSchema = z.object({
  categoryId: z.string().uuid(),
})

export type ApiDeleteCategoryResponse = z.infer<
  typeof ApiDeleteCategoryResponseSchema
>

export const ApiCreateCategoryRuleBodySchema = z.object({
  /** At least one: a rule with no include keywords can never match. */
  includeKeywords: KeywordListSchema.refine((list) => list.length > 0, {
    message: "At least one include keyword is required.",
  }),
  excludeKeywords: KeywordListSchema.default([]),
  priority: z.number().int().min(0).max(categoryRulePriorityMax).default(100),
  /** Limits the rule to this workspace. Omitted applies it in every
   * workspace that sees the category. */
  workspaceId: z.string().uuid().optional(),
})

export type ApiCreateCategoryRuleBody = z.infer<
  typeof ApiCreateCategoryRuleBodySchema
>

export const ApiUpdateCategoryRuleBodySchema = z.object({
  includeKeywords: KeywordListSchema.refine((list) => list.length > 0, {
    message: "At least one include keyword is required.",
  }).optional(),
  excludeKeywords: KeywordListSchema.optional(),
  priority: z
    .number()
    .int()
    .min(0)
    .max(categoryRulePriorityMax)
    .optional(),
})

export type ApiUpdateCategoryRuleBody = z.infer<
  typeof ApiUpdateCategoryRuleBodySchema
>

export const ApiDeleteCategoryRuleResponseSchema = z.object({
  ruleId: z.string().uuid(),
})

export type ApiDeleteCategoryRuleResponse = z.infer<
  typeof ApiDeleteCategoryRuleResponseSchema
>

export const ApiRestoreDefaultCategoriesResponseSchema = z.object({
  createdCategories: z.number().int().nonnegative(),
  createdRules: z.number().int().nonnegative(),
})

export type ApiRestoreDefaultCategoriesResponse = z.infer<
  typeof ApiRestoreDefaultCategoriesResponseSchema
>

export const ApiImportCategoriesModeSchema = z.enum(["preview", "commit"])

export type ApiImportCategoriesMode = z.infer<
  typeof ApiImportCategoriesModeSchema
>

/**
 * CSV columns: category, kind, include_keywords, exclude_keywords. Keywords
 * are pipe-separated within their cell ("gusto pay|paychex"); cells may be
 * quoted so a keyword can contain a comma. A header row is recognised and
 * skipped. Preview never writes; commit applies in one transaction.
 */
export const ApiImportCategoriesBodySchema = z.object({
  csv: z.string().min(1).max(categoriesCsvMaxLength),
  mode: ApiImportCategoriesModeSchema,
})

export type ApiImportCategoriesBody = z.infer<
  typeof ApiImportCategoriesBodySchema
>

export const ApiImportCategoriesRowActionSchema = z.enum([
  "create_category",
  "add_rule",
  "update_rule",
  "skip",
])

export type ApiImportCategoriesRowAction = z.infer<
  typeof ApiImportCategoriesRowActionSchema
>

/** One CSV line's verdict, the row the preview table renders. */
export const ApiImportCategoriesRowSchema = z.object({
  /** 1-based line in the pasted CSV, header included in the count. */
  line: z.number().int().positive(),
  category: z.string(),
  /** Null when the line's kind cell did not parse. */
  kind: ApiCategoryKindSchema.nullable(),
  action: ApiImportCategoriesRowActionSchema,
  /** Why a line was skipped; null for lines that apply cleanly. */
  reason: z.string().nullable(),
})

export type ApiImportCategoriesRow = z.infer<typeof ApiImportCategoriesRowSchema>

export const ApiImportCategoriesResponseSchema = z.object({
  mode: ApiImportCategoriesModeSchema,
  rows: z.array(ApiImportCategoriesRowSchema),
  createdCategories: z.number().int().nonnegative(),
  createdRules: z.number().int().nonnegative(),
  updatedRules: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
})

export type ApiImportCategoriesResponse = z.infer<
  typeof ApiImportCategoriesResponseSchema
>
